import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useDesktopAgentStatus } from "../hooks/useDesktopAgentStatus";
import { useTeamStore } from "../state/teamStore";
import type { Team } from "../types";

export default function TeamsHome() {
  const { teams, loading, initialized, fetchTeams } = useTeamStore();
  const { status: desktopStatus } = useDesktopAgentStatus();

  useEffect(() => {
    if (!initialized && !loading) {
      void fetchTeams();
    }
  }, [initialized, loading, fetchTeams]);

  if (!initialized || loading) {
    return (
      <div className="page">
        <div className="empty-state">
          <div className="spinner spinner-lg" />
          <div className="empty-state-title">Loading teams</div>
          <div className="empty-state-text">Loading your teams.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page page--wide">
      <header className="editorial-header">
        <div className="editorial-meta">
          <div className="editorial-meta-left">
            <span className="eyebrow">{teams.length} {teams.length === 1 ? "team" : "teams"}</span>
          </div>
          <div className="editorial-meta-right">
            <Link to="/onboarding/team/new" className="btn btn-primary">
              Create team
            </Link>
          </div>
        </div>
        <h1 className="editorial-title">Teams</h1>
        <p className="editorial-dek">
          Your teams, drivers, and series in one place.
        </p>
      </header>

      {teams.length > 0 && desktopStatus?.status !== "connected" && (
        <section className="section-shell" style={{ marginBottom: "1rem" }}>
          <div className="section-header">
            <div>
              <div className="section-kicker">Desktop Agent</div>
              <div className="section-heading">
                {desktopStatus?.status === "offline"
                  ? "Your Windows agent is offline."
                  : desktopStatus?.status === "needs_attention"
                  ? "Your Windows agent needs attention."
                  : "Install the Windows desktop agent next."}
              </div>
            </div>
          </div>
          <div
            className="card"
            style={{
              ...styles.desktopBanner,
              ...(desktopStatus?.status === "offline" || desktopStatus?.status === "needs_attention"
                ? { borderLeft: "3px solid var(--flag-yellow)" }
                : {}),
            }}
          >
            <div style={styles.desktopBannerCopy}>
              {desktopStatus?.status === "offline"
                ? "This account has used the Windows agent before, but it is not sending a recent heartbeat. Open it on the iRacing PC and turn the agent back on."
                : desktopStatus?.status === "needs_attention"
                ? "The app is installed, but watcher or telemetry capture is not healthy. Open the agent and fix it before the next session."
                : "Once you are on a team, the next step should be installing the Windows desktop agent so telemetry uploads stay automatic."}
            </div>
            <Link to="/desktop" className="btn btn-primary">
              Open desktop agent guide
            </Link>
          </div>
        </section>
      )}

      {teams.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-title">No teams yet</div>
          <div className="empty-state-text">
            Create your first team to start organizing weeks, comparing runs, and keeping notes in one place.
          </div>
          <Link to="/onboarding/team/new" className="btn btn-primary">
            Create your first team
          </Link>
        </div>
      ) : (
        <div style={styles.grid}>
          {teams.map((team) => (
            <TeamCard key={team.id} team={team} />
          ))}
          <Link to="/onboarding/team/new" style={{ textDecoration: "none" }}>
            <article style={styles.createCard}>
              <div style={styles.createIcon}>+</div>
              <div style={styles.createTitle}>Create Team</div>
              <div style={styles.createText}>
                Start a fresh team.
              </div>
            </article>
          </Link>
        </div>
      )}
    </div>
  );
}

function TeamCard({ team }: { team: Team }) {
  return (
    <Link to={`/teams/${team.id}`} style={{ textDecoration: "none" }}>
      <article className="card card-hover" style={styles.card}>
        <div style={styles.cardTop}>
          {team.logoUrl ? (
            <img src={team.logoUrl} alt="" style={styles.logo} />
          ) : (
            <div style={styles.logoFallback}>{team.name.charAt(0).toUpperCase()}</div>
          )}
          <div style={styles.cardTopCopy}>
            <div style={styles.cardTitle}>{team.name}</div>
            {team.description && <div style={styles.cardText}>{team.description}</div>}
          </div>
        </div>

        <div style={styles.cardFooter} className="num">
          {(team.memberCount ?? 0)} driver{(team.memberCount ?? 0) === 1 ? "" : "s"}
          {team.myRole && <span style={{ color: "var(--text-muted)" }}> · {team.myRole}</span>}
        </div>
      </article>
    </Link>
  );
}

const styles: Record<string, React.CSSProperties> = {
  titleSection: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "1.5rem",
    flexWrap: "wrap",
    paddingBottom: "1.5rem",
    borderBottom: "1px solid var(--rule-strong)",
    marginBottom: "1.5rem",
  },
  pageTitle: {
    fontFamily: "var(--font-display)",
    fontSize: "clamp(2.2rem, 4vw, 3rem)",
    fontWeight: 800,
    color: "var(--text-strong)",
    lineHeight: 1.05,
    letterSpacing: "-0.04em",
    marginBottom: "0.5rem",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "1rem",
  },
  card: {
    minHeight: "100%",
    padding: "1.5rem",
    display: "flex",
    flexDirection: "column",
    gap: "1.25rem",
  },
  cardTop: {
    display: "flex",
    alignItems: "flex-start",
    gap: "0.9rem",
  },
  cardTopCopy: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: "0.35rem",
  },
  logo: {
    width: "3.5rem",
    height: "3.5rem",
    borderRadius: "var(--r-lg)",
    objectFit: "cover",
    border: "1px solid var(--rule-strong)",
    flexShrink: 0,
  },
  logoFallback: {
    width: "3.5rem",
    height: "3.5rem",
    borderRadius: "var(--r-lg)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "var(--live-soft)",
    border: "1px solid var(--live-edge)",
    color: "var(--live-strong)",
    fontFamily: "var(--font-display)",
    fontSize: "1.2rem",
    fontWeight: 700,
    flexShrink: 0,
  },
  cardTitle: {
    fontFamily: "var(--font-display)",
    fontSize: "1.3rem",
    fontWeight: 700,
    color: "var(--text-strong)",
  },
  cardText: {
    color: "var(--text-secondary)",
    lineHeight: 1.6,
    fontSize: "0.9rem",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  cardFooter: {
    marginTop: "auto",
    borderTop: "1px solid var(--rule-faint)",
    paddingTop: "0.85rem",
    fontFamily: "var(--font-body)",
    fontSize: "0.8125rem",
    fontWeight: 500,
    color: "var(--text-secondary)",
  },
  createCard: {
    minHeight: "100%",
    padding: "1.5rem",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.75rem",
    borderRadius: "var(--r-lg)",
    border: "1px solid var(--rule)",
    textAlign: "center",
    cursor: "pointer",
    transition: "border-color 160ms ease, background-color 160ms ease",
  },
  createIcon: {
    width: "3rem",
    height: "3rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "999px",
    background: "rgba(255, 255, 255, 0.04)",
    border: "1px solid var(--rule-strong)",
    color: "var(--text-secondary)",
    fontSize: "1.5rem",
    fontWeight: 300,
  },
  createTitle: {
    fontFamily: "var(--font-display)",
    fontSize: "1.1rem",
    fontWeight: 700,
    color: "var(--text-strong)",
  },
  createText: {
    color: "var(--text-secondary)",
    fontSize: "0.85rem",
  },
  desktopBanner: {
    padding: "1.1rem 1.25rem",
    display: "flex",
    justifyContent: "space-between",
    gap: "1rem",
    alignItems: "center",
    flexWrap: "wrap",
  },
  desktopBannerCopy: {
    color: "var(--text-secondary)",
    lineHeight: 1.65,
    maxWidth: "48rem",
  },
};
